import { N8N_TEAMS_URL } from '@/api/n8n'

// Fase 4 do plano de otimização de carregamento (docs/planos/PLANO-OTIMIZACAO-CARREGAMENTO-DASHBOARD.md,
// reaberta em 2026-09-03) — descobrir se as chamadas concorrentes ao webhook
// n8n compartilhado (N8N_TEAMS_URL) na rajada pós-login realmente rodam em
// paralelo ou ficam em fila (do próprio n8n, ou do limite de conexões por
// origem do navegador — HTTP/1.1 permite ~6 simultâneas por host). Usa a
// Resource Timing API do navegador: startTime/responseEnd continuam expostos
// mesmo cross-origin sem header Timing-Allow-Origin (só o detalhamento fino —
// DNS/TCP/TTFB — fica zerado sem esse header), o que já basta pra ver se os
// intervalos [start, end] se sobrepõem.

interface N8nCallLabel {
  command: string
  start: number
}

// Cada service define seu próprio `post()`/`fetchWithRetry()` local pro mesmo
// WEBHOOK (não há um client único a interceptar sem tocar ~30 call sites —
// ver CLAUDE.md) — pra saber qual comando cada entrada de timing representa
// sem mexer em nenhum desses arquivos, um tap opcional em window.fetch lê o
// body de cada POST pro webhook compartilhado e casa por proximidade de
// timestamp com a entrada correspondente na Resource Timing API.
const callLog: N8nCallLabel[] = []
const MAX_LOG = 100
let patched = false

export function installN8nBurstFetchTap(): void {
  if (patched || typeof window === 'undefined' || typeof window.fetch !== 'function') return
  patched = true

  // O buffer de Resource Timing do navegador é limitado (~250 entradas por
  // padrão) e, numa sessão já ativa (realtime, imagens de mensagem, polling
  // de várias telas), enche rápido — depois disso o navegador simplesmente
  // PARA de registrar entradas novas (mesmo de chamadas que acabaram de
  // acontecer), o que fazia getN8nBurstTiming() voltar vazio mesmo com
  // get_conversations tendo rodado segundos antes. Aumenta bem a margem.
  try {
    performance.setResourceTimingBufferSize?.(2000)
  } catch {
    // não suportado neste navegador — segue sem aumentar o buffer
  }

  const originalFetch = window.fetch.bind(window)
  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url
      const body = init?.body
      if (url?.startsWith(N8N_TEAMS_URL) && typeof body === 'string') {
        const parsed = JSON.parse(body)
        if (parsed?.command) {
          callLog.push({ command: String(parsed.command), start: performance.now() })
          if (callLog.length > MAX_LOG) callLog.shift()
        }
      }
    } catch {
      // melhor esforço — nunca deve impedir o fetch real de acontecer
    }
    return originalFetch(input as RequestInfo, init)
  }) as typeof window.fetch
}

export interface N8nBurstEntry {
  command: string
  start: number
  end: number
  duration: number
}

export function getN8nBurstTiming(windowMs = 15000): N8nBurstEntry[] {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return []
  const cutoff = performance.now() - windowMs
  const entries = performance
    .getEntriesByType('resource')
    .filter((e): e is PerformanceResourceTiming => e.entryType === 'resource' && e.name.startsWith(N8N_TEAMS_URL) && e.startTime >= cutoff)
    .map(e => ({ start: Math.round(e.startTime), end: Math.round(e.responseEnd), duration: Math.round(e.duration) }))
    .sort((a, b) => a.start - b.start)

  // Casa cada entrada com o comando de start mais próximo no log do tap
  // (só populado se installN8nBurstFetchTap() rodou antes da rajada) —
  // greedy por proximidade, cada registro do log é usado no máximo uma vez.
  const pool = [...callLog]
  return entries.map(e => {
    let bestIdx = -1
    let bestDiff = Infinity
    pool.forEach((c, i) => {
      const diff = Math.abs(c.start - e.start)
      if (diff < bestDiff) { bestDiff = diff; bestIdx = i }
    })
    const command = bestIdx !== -1 && bestDiff < 2000 ? pool.splice(bestIdx, 1)[0].command : 'desconhecido (tap não ativo?)'
    return { command, ...e }
  })
}

/**
 * Imprime a tabela de chamadas + um veredito: se pelo menos duas chamadas
 * têm intervalos [start,end] sobrepostos, roda paralelismo real; se nenhuma
 * se sobrepõe apesar de terem sido disparadas quase juntas, é sinal de fila.
 */
export function logN8nBurstTiming(windowMs = 15000): void {
  const entries = getN8nBurstTiming(windowMs)
  if (!entries.length) {
    console.info('[n8n-timing] nenhuma chamada ao webhook compartilhado encontrada na janela.')
    return
  }
  console.table(entries)
  const span = Math.max(...entries.map(e => e.end)) - Math.min(...entries.map(e => e.start))
  const sumDuration = entries.reduce((s, e) => s + e.duration, 0)

  if (entries.length < 2) {
    console.info(`[n8n-timing] só 1 chamada nessa janela — nada pra comparar sobreposição (não é sinal de fila).`)
    return
  }

  const overlaps = entries.some((e, i) => entries.slice(0, i).some(prev => e.start < prev.end))
  console.info(
    `[n8n-timing] ${entries.length} chamadas · janela total ${span}ms · soma das durações ${sumDuration}ms · ` +
    (overlaps
      ? 'HÁ sobreposição — pelo menos parte roda em paralelo de verdade.'
      : 'SEM sobreposição — cada chamada só começa depois da anterior terminar (fila no navegador ou no n8n).')
  )
}
